import { FC, useEffect, useMemo } from "react"
import {
  Link,
  Outlet,
  useLocation,
  useMatch,
  useNavigate
} from "react-router-dom"
import {
  useAppDispatch,
  useAppSelector,
  useInMobileView
} from "../../common/hooks"
import { Dropdown, If, PageHeader } from "../../common/ui"
import { checkIsBankAdmin } from "../../common/utils"
import {
  useFetchBrandQuery,
  useFetchBrandsQuery,
  useFetchLoanOfficerQuery
} from "../../redux/queries"
import {
  getMe,
  selectPasswordUpdated,
  selectProfileUpdated,
  selectUserDetails
} from "../../redux/slices/auth"
import { ChevronDown } from "lucide-react"

type Props = {
  title?: string
}

type SectionType =
  | "settings"
  | "lo_profile"
  | "bank_billing"
  | "payment_management"
  | "email_preferences"
  | "usage"

interface ISection {
  key: SectionType
  name: string
  path: string
}

const SECTIONS: ISection[] = [
  {
    key: "settings",
    path: "/account",
    name: "My account settings"
  },
  {
    key: "lo_profile",
    path: "/account/lo-profile",
    name: "My loan officer profile"
  },
  {
    key: "bank_billing",
    path: "/account/billing",
    name: "Billing"
  },
  {
    key: "payment_management",
    path: "/account/payment-management",
    name: "Payment Settings"
  },
  {
    key: "email_preferences",
    path: "/account/email-preferences",
    name: "Email preferences"
  },
  {
    key: "usage",
    path: "/account/usage",
    name: "Usage"
  }
]

const AccountPage: FC<Props> = ({ title = "Account" }) => {
  const dispatch = useAppDispatch()

  const inMobileView = useInMobileView()
  const passwordUpdated = useAppSelector(selectPasswordUpdated)
  const profileUpdated = useAppSelector(selectProfileUpdated)

  const user = useAppSelector(selectUserDetails)

  const { data: loProfile } = useFetchLoanOfficerQuery(user?.id ?? "", {
    skip: !user?.id
  })

  const isBankAdmin = checkIsBankAdmin(user?.roles || [])

  const { data: brands } = useFetchBrandsQuery({})

  const selectedBrand = useMemo(
    () => brands?.find(b => b.id === loProfile?.bank_nid),
    [brands, loProfile?.bank_nid]
  )
  const { data: apiBrand } = useFetchBrandQuery(loProfile?.bank_nid ?? "", {
    skip: !loProfile?.bank_nid || !!selectedBrand?.id
  })

  const brand = selectedBrand || apiBrand

  useEffect(() => {
    profileUpdated && dispatch(getMe())
  }, [dispatch, profileUpdated])

  let location = useLocation()

  const navigate = useNavigate()

  const match = useMatch("/account/billing")

  const matchInvoice = useMatch("/account/billing/:_id")

  if (!user) return null

  if (!isBankAdmin && match) {
    navigate("/account")
  }

  return (
    <>
      <div className="p-6 pt-4 sm:pt-6 border-neutral-200">
        <PageHeader title={title} />
      </div>
      <div
        className={`${
          matchInvoice && inMobileView ? "" : "bg-primary-100 px-4 py-6 md:px-6"
        } flex-grow`}
      >
        <div className="lg:flex items-start gap-6">
          <div className="w-64 bg-white p-4 rounded-lg hidden lg:block">
            {SECTIONS.map(({ key, path, name }) => {
              const isActive = location.pathname.endsWith(path)

              if (key === "bank_billing" && !isBankAdmin) return null
              if (key === "payment_management" && !isBankAdmin) return null
              if (key === "usage" && !isBankAdmin) return null
              return (
                <Link
                  key={key}
                  className={`block text-sm font-medium py-2 px-3 rounded-md cursor-pointer ${
                    isActive ? "bg-neutral-100" : "bg-white"
                  }`}
                  to={path}
                >
                  {name}
                </Link>
              )
            })}
          </div>

          <If
            condition={!matchInvoice}
            then={
              <div className="flex lg:hidden mb-4">
                <Dropdown
                  className="mr-auto"
                  items={SECTIONS.filter(({ key }) => {
                    if (key === "bank_billing" && !isBankAdmin) return false
                    if (key === "payment_management" && !isBankAdmin)
                      return false
                    if (key === "usage" && !isBankAdmin) return false
                    return true
                  }).map(({ key, path, name }) => ({
                    name,
                    onClick: () => navigate(path)
                  }))}
                  menuBtn={
                    <div className="flex items-center justify-between w-full bg-white p-4 rounded-lg">
                      <span className="text-sm font-medium">
                        {SECTIONS.find(({ path }) =>
                          location.pathname.endsWith(path)
                        )?.name ?? "Billing"}
                      </span>
                      <ChevronDown size={20} />
                    </div>
                  }
                />
              </div>
            }
          />
          <div className="flex-1 rounded-lg bg-white">
            <Outlet
              context={{ user, passwordUpdated, profile: loProfile, brand }}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default AccountPage
